// Phase 2D.2B — the pending-registration payload that must survive e-mail
// confirmation.
//
// The organization is created only AFTER Supabase verifies the e-mail address,
// which can happen in another tab, another browser or an external mail client.
// React state, sessionStorage, localStorage and query parameters all die on that
// boundary, so the two non-sensitive onboarding fields travel in Supabase Auth
// USER METADATA, which is returned with the verified session wherever it is
// established. There is no server-side pending-registration table and this phase
// deliberately adds no migration.
//
// SECURITY: user metadata is user-writable, therefore UNTRUSTED. Only the two
// display fields are ever read, they are re-sanitized here, and the backend
// validates them again. Nothing here can carry a role, a seat entitlement, a
// membership, a license status, a platform-admin flag, a password or a token —
// the role is assigned server-side by ``bootstrap_organization`` and the acting
// user always comes from the verified JWT.
//
// This module is intentionally dependency-free and side-effect-free so it can be
// executed and audited in isolation.

export const SIGNUP_CONFIRM_PATH = "/auth/confirm-signup";

// The metadata key holding the pending onboarding payload.
export const ONBOARDING_KEY = "offertrace_onboarding";

export const SIGNUP_COOLDOWN_KEY = "offertrace.cooldown.signup_confirmation_link";

export const ONBOARDING_MAX_LENGTH = 200;

// The redirect target is derived from the current origin only — never from a
// query parameter, a fragment, storage or any other user-supplied value, so no
// open redirect is possible.
export function signupConfirmRedirectUrl() {
  return `${window.location.origin}${SIGNUP_CONFIRM_PATH}`;
}

function sanitize(value) {
  if (typeof value !== "string") return "";
  return value
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .trim()
    .slice(0, ONBOARDING_MAX_LENGTH);
}

export function buildOnboardingMetadata({ fullName, organizationName }) {
  return {
    [ONBOARDING_KEY]: {
      version: 1,
      full_name: sanitize(fullName),
      organization_name: sanitize(organizationName),
    },
  };
}

export function readPendingOnboarding(user) {
  const raw = user && user.user_metadata ? user.user_metadata[ONBOARDING_KEY] : null;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const organizationName = sanitize(raw.organization_name);
  if (!organizationName) return null;
  return { fullName: sanitize(raw.full_name), organizationName };
}

// Marks the payload consumed. The backend bootstrap is idempotent, so failing to
// clear it can never create a second organization.
export function clearedOnboardingMetadata() {
  return { [ONBOARDING_KEY]: null };
}
